/**
 * Bulk Import — Excel upload for users, customers, and devices.
 * Route: /bulk-import
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { AppBadge, AppButton, AppCard, AppField } from "../../../shared/components/ui";
import { IconCheck, IconError, IconSpinner } from "../../../shared/components/icons";
import { PageHeader } from "../../../shared/components/PageChrome";
import { api, ApiError } from "../../../shared/api/client";
import { getUser } from "../../auth/session";

type Kind = "users" | "customers" | "devices";

type UploadResult = {
  upload_id?: number;
  kind: string;
  status: string;
  total: number;
  inserted: number;
  skipped_duplicates: number;
  failed: number;
  message?: string;
  errors?: { row: number; reason: string }[];
};

const KIND_COPY: Record<Kind, { title: string; blurb: string }> = {
  users: {
    title: "Import users",
    blurb: "Download the Excel template, add staff rows, then upload.",
  },
  customers: {
    title: "Import customers",
    blurb: "Download the template, fill one row per household, then upload. Phone must be +255XXXXXXXXX.",
  },
  devices: {
    title: "Import devices",
    blurb:
      "Download the template. Use device_type codes that already exist (e.g. H4G-150). PayGO rows need a 32-char hex secret_key + starting_code. Change serials if re-uploading — duplicates are skipped, not inserted again.",
  },
};

function templatePath(kind: Kind): string {
  return kind === "users" ? "/users/bulk/template" : `/bulk/templates/${kind}`;
}

function uploadPath(kind: Kind): string {
  return kind === "users" ? "/users/bulk" : `/bulk/${kind}`;
}

export function BulkImport() {
  const user = getUser();
  const canUsers = user?.role === "system_admin" || user?.role === "business_admin";
  const kinds = (canUsers ? ["users", "customers", "devices"] : ["customers", "devices"]) as Kind[];
  const [kind, setKind] = useState<Kind>(canUsers ? "users" : "customers");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState<"idle" | "template" | "upload">("idle");
  const [error, setError] = useState("");
  const [result, setResult] = useState<UploadResult | null>(null);

  const downloadTemplate = async () => {
    setBusy("template");
    setError("");
    try {
      await api.download(templatePath(kind), `${kind}_template.xlsx`);
    } catch (ex: unknown) {
      setError(ex instanceof Error ? ex.message : "Template download failed");
    } finally {
      setBusy("idle");
    }
  };

  const upload = async () => {
    if (!file) {
      setError("Choose an .xlsx file first");
      return;
    }
    setBusy("upload");
    setError("");
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const data = await api.upload<UploadResult>(uploadPath(kind), form);
      setResult({ ...data, kind });
      setFile(null);
    } catch (ex: unknown) {
      if (ex instanceof ApiError && ex.details && typeof ex.details === "object") {
        const d = ex.details as Partial<UploadResult> & { error?: string };
        if (d.total != null || d.upload_id != null) {
          setResult({
            upload_id: d.upload_id,
            kind,
            status: d.status ?? "failed",
            total: d.total ?? 0,
            inserted: d.inserted ?? 0,
            skipped_duplicates: d.skipped_duplicates ?? 0,
            failed: d.failed ?? 0,
            message: d.message,
            errors: d.errors,
          });
        }
        setError(d.error ?? d.message ?? ex.message);
      } else {
        setError(ex instanceof Error ? ex.message : "Upload failed");
      }
    } finally {
      setBusy("idle");
    }
  };

  const copy = KIND_COPY[kind];
  const kindLabel = (k: Kind) =>
    k === "users" ? "Users" : k === "customers" ? "Customers" : "Devices";

  return (
    <div>
      <PageHeader
        title="Bulk import"
        subtitle="Import users, customers, or devices from Excel — not leads."
      />

      <div className="pg-filter-chips" style={{ marginBottom: 16 }}>
        {kinds.map((k) => (
          <button
            key={k}
            type="button"
            className={`pg-chip${kind === k ? " active" : ""}`}
            onClick={() => {
              setKind(k);
              setResult(null);
              setError("");
              setFile(null);
            }}
          >
            {kindLabel(k)}
          </button>
        ))}
      </div>

      <AppCard title={copy.title}>
        <p className="pg-text-muted">{copy.blurb}</p>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
          <AppButton type="button" variant="secondary" onClick={downloadTemplate} disabled={busy !== "idle"}>
            {busy === "template" ? <><IconSpinner size={14} /> Preparing…</> : "Download template"}
          </AppButton>
        </div>

        <div style={{ marginTop: 16 }}>
          <AppField label="Upload filled .xlsx">
            <input
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              disabled={busy !== "idle"}
            />
          </AppField>
          {file && (
            <p className="pg-text-muted" style={{ marginTop: 4 }}>
              Selected: {file.name} ({Math.round(file.size / 1024)} KB)
            </p>
          )}
          <AppButton type="button" onClick={upload} disabled={busy !== "idle" || !file} style={{ marginTop: 8 }}>
            {busy === "upload" ? <><IconSpinner size={14} /> Uploading…</> : "Upload & import"}
          </AppButton>
        </div>

        {error && (
          <div className="pg-inline-error" role="alert" style={{ marginTop: 12 }}>
            <IconError size={14} /> {error}
          </div>
        )}

        {result && (
          <div style={{ marginTop: 16 }}>
            <h4 style={{ marginBottom: 8 }}>Import result</h4>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
              <AppBadge tone={result.status === "completed" ? "success" : result.status === "partial" ? "warning" : "error"}>
                {result.status}
              </AppBadge>
              {result.upload_id != null && (
                <AppBadge tone="info">Upload #{result.upload_id}</AppBadge>
              )}
            </div>
            {result.message && <p className="pg-text-muted">{result.message}</p>}
            {result.inserted === 0 && result.skipped_duplicates > 0 && result.failed === 0 ? (
              <p className="pg-text-muted" style={{ marginBottom: 8 }}>
                No new devices were added — every row matched an existing serial. Use new serial numbers
                (or delete the sample OPG-TEST-* rows if they were already imported).
              </p>
            ) : null}
            {result.inserted === 0 && result.failed > 0 ? (
              <p className="pg-text-muted" style={{ marginBottom: 8 }}>
                Nothing was inserted. Fix the row errors below (common: wrong device_type, or PayGO
                secret_key not exactly 32 hex characters).
              </p>
            ) : null}
            <table className="pg-table">
              <tbody>
                <tr><td>Total rows</td><td style={{ textAlign: "right" }}>{result.total}</td></tr>
                <tr><td>Inserted</td><td style={{ textAlign: "right" }}><IconCheck size={12} /> {result.inserted}</td></tr>
                <tr><td>Skipped duplicates</td><td style={{ textAlign: "right" }}>{result.skipped_duplicates}</td></tr>
                <tr><td>Failed</td><td style={{ textAlign: "right" }}>{result.failed}</td></tr>
              </tbody>
            </table>
            {result.errors && result.errors.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <p className="pg-text-muted">First errors:</p>
                <ul style={{ margin: 0, paddingLeft: "1.25rem", fontSize: 14 }}>
                  {result.errors.slice(0, 10).map((e) => (
                    <li key={`${e.row}-${e.reason}`}>Row {e.row}: {e.reason}</li>
                  ))}
                </ul>
              </div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              {kind === "users" ? (
                <Link to="/admin/users"><AppButton type="button">View users</AppButton></Link>
              ) : kind === "customers" ? (
                <Link to="/customers"><AppButton type="button">View customers</AppButton></Link>
              ) : (
                <Link to="/devices"><AppButton type="button">View devices</AppButton></Link>
              )}
            </div>
          </div>
        )}
      </AppCard>
    </div>
  );
}
